import '@/styles/global.css'
import '@/styles/customStyleInput.css'
import '@/styles/openTab.css'
import '@/styles/popup.css'
import '@/styles/attention.css'
import '@/styles/audio-outputs.css'
import '@/styles/ParticipantList.css'
import '@/styles/globalsStyle.scss'
import '@/styles/menu.css'

import '@livekit/components-styles'
import '@livekit/components-styles/prefabs'

import { useRouter } from 'next/router'

import { useEffect } from 'react'
import { Provider } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ToastContainer, ToastIcon } from 'react-toastify'
import { parseCookie } from 'next/dist/compiled/@edge-runtime/cookies'

import jwt from 'jsonwebtoken'

import { Bell } from '@/src'
import { Seo } from '@/src/components/app/Seo/Seo'
import { MeetData } from '@/src/components/app/MeetData/MeetData'
import ErrorBoundary from '@/src/components/ErrorBoundary/ErrorBoundary'

import '@/src/i18n'

import { getClientPromptProps } from '@/src/lib/clientPrompt'

import { RoomLookupErrorKind } from '@/src/lib/roomLookupError'

import { initialBranding } from '@/src/constants/branding'
import { isSamoletProjectMode, resolveProjectMode } from '@/src/constants/projectModes'

import { Ctx, IPrompt, List, MyAppProps, Res } from '@/src/types/app.type'
import { ITypeOfMeet } from '@/src/types/typeOfMeet.type'
import { IMeetCodes, ISettings } from '@/src/types/meetData.type'

import { store } from '@/src/store/rootReducer'

MyApp.getInitialProps = async ({ ctx }: Ctx) => {
  let brandingData: Res<List>
  let settingsData: ISettings | undefined
  let promptData: string | IPrompt = ''
  let typesOfMeet: ITypeOfMeet[] = []
  let meetCodes: IMeetCodes = { company_id: null, meet_id: null }
  // Почему не удалось загрузить комнату: PreJoin покажет оверлей вместо входа в звонок.
  let roomLookupError: RoomLookupErrorKind | null = null

  if (!ctx.req) {
    return {
      data: {},
      typesOfMeet: [],
      meetCodes,
      roomType: null,
      roomName: '',
      project: null,
      v: null,
      referrer: null,
      samoletUnavailable: false,
      roomLookupError: null,
    }
  }

  if (ctx.pathname === '/_error') {

    return {
      data: { ...initialBranding },
      typesOfMeet: [],
      meetCodes,
      roomType: null,
      roomName: '',
      project: null,
      v: null,
      referrer: null,
      prefilledUsername: null,
      hiddenButtons: { screenShare: false, chat: false, leave: false },
      samoletUnavailable: false,
      roomLookupError: null,
    }
  }

  let path = `/${ctx.query.companySlug}/${ctx.query.name}`
  if (!ctx.query.name) {
    path = `/${ctx.query.companySlug}`
  }

  const roomNameFromHeaders = ctx.req?.headers['x-room-name'] as string
  const roomTypeFromHeaders = ctx.req?.headers['x-room-type'] as string
  const meetTokenFromHeaders = ctx.req?.headers['x-meet-token'] as string
  // Headers для короткой ссылки
  const projectFromHeaders = ctx.req?.headers['x-project'] as string
  const avatarFromHeaders = ctx.req?.headers['x-avatar'] as string
  const vFromHeaders = ctx.req?.headers['x-v'] as string
  const isolateFromHeaders = ctx.req?.headers['x-isolate'] as string

  const cookies = parseCookie(ctx?.req?.headers?.cookie || '')
  const cookieToken = cookies?.get(`meet_token:${path}`)
  const cookieRoomType = cookies.get('room_type')
  const cookieReferrer = cookies.get('x-referrer')

  const finalToken = meetTokenFromHeaders || cookieToken
  const finalRoomType = roomTypeFromHeaders || cookieRoomType

  const roomNameFromQuery =
    typeof ctx.query?.room === 'string'
      ? ctx.query.room
      : typeof ctx.query?.name === 'string'
      ? ctx.query.name
      : undefined

  let roomName: string = roomNameFromHeaders || roomNameFromQuery || ''

  if (!roomName && finalToken) {
    const decodedCookies = jwt.decode(finalToken)
    if (typeof decodedCookies === 'string') {
      roomName = decodedCookies
    } else {
      roomName = decodedCookies?.roomName
    }
  }

  // Нормализуем project из query/headers, чтобы ветки интеграций выбирались предсказуемо.
  const projectMode = resolveProjectMode(
    (typeof ctx.query?.project === 'string' ? ctx.query.project : null) ?? projectFromHeaders,
  )
  
  if (isSamoletProjectMode(projectMode)) {
    try {
      const [brandingMeetsResp, settingsResp] = await Promise.all([
        fetch(`${process.env.NEXT_PUBLIC_HURMA_API}/branding_meets?roomName=${roomName}`),
        fetch(`${process.env.NEXT_PUBLIC_HURMA_API}/meet/setting/${ctx.query.name}`),
      ])

      if (brandingMeetsResp) {
        const branding = await brandingMeetsResp.json()
        if (branding?.data?.list && branding.data.list.length > 0) {
          brandingData = branding as Res<List>
        } else if (branding?.data) {
          brandingData = {
            status: true,
            data: { list: [branding.data] },
          } as Res<List>
        }
      }

      const settings = await settingsResp?.json()
      const rawSettingsPayload = settings?.data || settings
      settingsData = (rawSettingsPayload?.settings || rawSettingsPayload) as ISettings
      promptData = (rawSettingsPayload?.prompt || '') as string | IPrompt
    } catch (error) {
      console.error(error)
    }
  }

  const finalBrandingData = brandingData?.data?.list?.[0]

  // Extract and combine user_name and user_phone
  const userName = typeof ctx.query?.user_name === 'string' ? ctx.query.user_name : null
  const userPhone = typeof ctx.query?.user_phone === 'string' ? ctx.query.user_phone : null
  let prefilledUsername: string | null = null
  
  if (userName && userPhone) {
    prefilledUsername = `${userName} ${userPhone}`
  } else if (userName) {
    prefilledUsername = userName
  } else if (userPhone) {
    prefilledUsername = userPhone
  }

  // Проверяем, является ли это голосовым режимом Федора
  const isFedorVoiceMode = finalRoomType === 'voiceAssistant' || finalRoomType === 'voiceAssistant2'
  const companySlug = typeof ctx.query?.companySlug === 'string' ? ctx.query.companySlug : null
  const hasRoomPath = typeof ctx.query?.name === 'string'
  const samoletUnavailable =
    companySlug === 'samolet' && hasRoomPath && finalRoomType !== 'textAssistant'

  // Параметры скрытия кнопок
  // Для голосового режима Федора автоматически скрываем кнопки демонстрации экрана и чата
  const hiddenButtons = {
    screenShare: ctx.query?.hide_screen_share === 'true' || isFedorVoiceMode,
    chat: ctx.query?.hide_chat === 'true' || isFedorVoiceMode,
    leave: ctx.query?.hide_leave === 'true',
  }

  const data = {
    data: {
      ...initialBranding,
      ...finalBrandingData,
      settings: settingsData,
      ...getClientPromptProps(finalRoomType, promptData),
    },
    typesOfMeet: typesOfMeet ?? [],
    meetCodes,
    roomType: finalRoomType === 'null' ? null : finalRoomType || null,
    roomName,
    // Используем значения из headers (для коротких ссылок) или из query
    project: projectMode ?? (ctx.query?.project ?? projectFromHeaders ?? null),
    avatar: ctx.query?.avatar ?? avatarFromHeaders ?? null,
    v: ctx.query?.v ?? vFromHeaders ?? null,
    referrer: cookieReferrer || null,
    prefilledUsername,
    hiddenButtons,
    samoletUnavailable,
    roomLookupError,
  }

  return data
}

function MyApp({
  Component,
  pageProps,
  typesOfMeet,
  meetCodes,
  roomName,
  roomType,
  project,
  avatar,
  v,
  referrer,
  prefilledUsername,
  hiddenButtons,
  samoletUnavailable,
  roomLookupError,
  data,
}: MyAppProps) {
  const { i18n } = useTranslation()
  const router = useRouter()

  useEffect(() => {
    if (router.query?.lang !== 'ru') {
      i18n.changeLanguage(router.query?.lang as string)
    }

    sessionStorage.setItem('settings', JSON.stringify(data?.settings ?? {}))
    sessionStorage.setItem('autoRecord', JSON.stringify(data?.settings?.auto_rec ?? false))
    sessionStorage.setItem('waitRoom', JSON.stringify(data?.settings?.wait_room ?? false))

    const userChoices = JSON.parse(localStorage.getItem('lk-user-choices') || '{}')
    const newUserChoices = {
      ...userChoices,
    }

    if (!data?.img_background_speaker && userChoices.currentBgEffect?.options?.imagePath) {
      newUserChoices.currentBgEffect = { type: 'none', options: {} }
    }

    localStorage.setItem('lk-user-choices', JSON.stringify(newUserChoices))
  }, [])

  const ToastIcon: ToastIcon = ({ type, theme }) => {
    switch (type) {
      case 'info':
        return <Bell className="toast-icon" />
      case 'error':
        return <Bell className="toast-icon" />
      case 'success':
        return <Bell className="toast-icon" />
      case 'warning':
        return <Bell className="toast-icon" />
      default:
        return null
    }
  }

  return (
    <>
      <Seo {...data} />

      <ErrorBoundary>
        <Provider store={store}>
          <MeetData
            typesOfMeet={typesOfMeet}
            meetCodes={meetCodes}
            roomName={roomName}
            roomType={roomType}
            project={project}
            avatar={avatar}
            v={v}
            referrer={referrer}
            prefilledUsername={prefilledUsername}
            hiddenButtons={hiddenButtons}
            {...data}
          >
            <Component
              {...pageProps}
              samoletUnavailable={samoletUnavailable}
              roomLookupError={roomLookupError}
            />
            <div id="modal-root" />
            <ToastContainer className="toast-container" icon={ToastIcon} autoClose={10000} />
          </MeetData>
        </Provider>
      </ErrorBoundary>
    </>
  )
}

export default MyApp
